import { Box, Button, Typography } from "@mui/material";
import { useState } from "react";
import { useMutation, useQuery } from "react-query";
import { ResType } from "types";
import { getClientDemands, getGstrProfile, exportclientDemand } from "api/services/gstr/gstr";
import { useParams } from "react-router-dom";
import Table, { ColumnType } from "components/Table";
import _ from "lodash";
import GstTaxBreakdownDialog from "../GstTaxBreakdownDialog";
import moment from "moment";
import { formattedAmount } from "utils/formattedAmount";
import { getCurrentFormattedDateTime } from "utils";
import { snack } from "components/toast";
import { handleError } from "utils/handleError";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { SortDirection } from "data/constants";


type TaxType = "IGST" | "CGST" | "SGST" | "CESS" | "TOTAL";

const ClientDemands = () => {
  const [page, setPage] = useState<number>(0);
  const [pageCount, setPageCount] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);
  const params = useParams();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [taxType, setTaxType] = useState<TaxType>("IGST");

  const { data, isLoading }: ResType = useQuery(
    ["get-gst-client-demand", params.gstrid, { offset: pageCount * page, limit: pageCount }],
    getClientDemands,
    {
      onSuccess: (res) => {
        setTotalCount(res?.data?.count);
      },
    }
  );

    const { data: clientdata }: ResType = useQuery(["gstr-profile", params.gstrid], getGstrProfile, {
  
    });
    const clientName = clientdata?.data?.gstrProfile?.legalName;
    const { mutate: mutate1 } = useMutation(exportclientDemand, {
      onSuccess: (res: any) => {
        const arr = new Uint8Array(res.data?.data);
        const blob = new Blob([arr], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const file = window.URL.createObjectURL(blob);
        let link = document.createElement("a");
        const formattedDateTime = getCurrentFormattedDateTime();
        link.href = file;
        link.download = `ATOM Pro - ${clientName} GST Outstanding Demands ${formattedDateTime}.xlsx`;
        link.click();
        snack.success("GST Outstanding Demands exported successfully");
      },
      onError: (err: any) => {
        snack.error(err);
        snack.error(handleError(err));
      },
    });
  
    const handleExport = () => {
      mutate1({
        gstrid: params.gstrid,
      });
    }

  const defaultColumns: Array<ColumnType> = [
    {
      key: "demandDt",
      title: "Demand Date",
      render: (v) => {
        return (
          <Typography>
            {v?.demandDt ? moment(parseInt(v?.demandDt)).format("DD MMM YYYY") : "-"}
          </Typography>
        );
      },
      sort: true,
      active: false,
      direction: SortDirection.ASC,
      default: true,
    },
    {
      key: "orderNo",
      title: "Order Number",
    },
    {
      key: "igstTot",
      title: (
        <Box display="flex" justifyContent='flex-end' width="100%">
          IGST 
        </Box>
      ),
      render: (v) => (
        <Typography
          sx={{ cursor: "pointer", color: "primary.main" }}
          onClick={() => handleTaxClick(v, "IGST")} align="right"
        >
          {formattedAmount(v?.igstTot) || "-"}
        </Typography>
      ),
      sort: true,
      active: false,
      direction: SortDirection.ASC,
      default: true,
    },
    {
      key: "cgstTot",
      title: (
        <Box display="flex" justifyContent='flex-end' width="100%">
          CGST 
        </Box>
      ),
      render: (v) => (
        <Typography
          sx={{ cursor: "pointer", color: "primary.main" }}
          onClick={() => handleTaxClick(v, "CGST")} align="right"
        >
          {formattedAmount(v?.cgstTot) || "-"}
        </Typography>
      ),
      sort: true,
      active: false,
      direction: SortDirection.ASC,
      default: true,
    },
        {
      key: "sgstTot",
      title: (
        <Box display="flex" justifyContent='flex-end' width="100%">
          SGST 
        </Box>
      ),
      render: (v) => (
        <Typography
          sx={{ cursor: "pointer", color: "primary.main" }}
          onClick={() => handleTaxClick(v, "SGST")} align="right"
        >
          {formattedAmount(v?.sgstTot) || "-"}
        </Typography>
      ),
      sort: true,
      active: false,
      direction: SortDirection.ASC,
      default: true,
    },
    {
      key: "cessTot",
      title: (
        <Box display="flex" justifyContent='flex-end' width="100%">
          CESS 
        </Box>
      ),
      render: (v) => (
        <Typography
          sx={{ cursor: "pointer", color: "primary.main" }}
          onClick={() => handleTaxClick(v, "CESS")} align="right"
        >
          {formattedAmount(v?.cessTot) || "-"}
        </Typography>
      ),
      sort: true,
      active: false,
      direction: SortDirection.ASC,
      default: true,
    },
    {
      key: "totalTot",
      title: (
        <Box display="flex" justifyContent='flex-end' width="100%">
          Total 
        </Box>
      ),
      render: (v) => (
        <Typography
          sx={{ cursor: "pointer", color: "primary.main" }}
          onClick={() => handleTaxClick(v, "TOTAL")} align="right"
        >
          {formattedAmount(v?.totalTot) || "-"}
        </Typography>
      ),
      sort: true,
      active: false,
      direction: SortDirection.ASC,
      default: true,
    },
  ];
  const [columns, setColumns] = useState(_.cloneDeep(defaultColumns));

  const handleTaxClick = (row: any, type: TaxType) => {
    setSelectedRow(row);
    setTaxType(type);
    setDialogOpen(true);
  };

  return (
    <Box p={1} >
       <Box display="flex" justifyContent="flex-end" sx={{ p: 1 }}>
          <Button variant="outlined" color="secondary" onClick={handleExport}
            startIcon={<FileDownloadOutlinedIcon />}>
            Export
          </Button>
        </Box>
      <Table
        sx={{ pb: 0, mb: 0, height: "450px"}}
        pagination={{ totalCount, setPageCount, pageCount, page, setPage }}
        data={data?.data?.result || []}
        columns={columns}
        loading={isLoading}
      />
      <GstTaxBreakdownDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        row={selectedRow}
        type={taxType}
      />
    </Box>
  );
};

export default ClientDemands;
